import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useLeadNotes } from '../hooks/useLeadNotes';
import { useAddLeadNote } from '../hooks/useAddLeadNote';
import { AddNoteForm } from './AddNoteForm';
import { NoteCard } from './NoteCard';

interface LeadNotesSectionProps {
  leadId: number;
}

/**
 * Collapsible section showing notes for a lead
 * - Header with count badge
 * - Add note form at top
 * - List of note cards below
 *
 * Collapsed by default. Expanded, this section is 452px tall and is the single
 * largest contributor to the Leads page scrolling — measured against the legacy
 * screen, which fits the same workflow on one screen. Collapsing it by default
 * reclaims 376px. The count badge stays visible on the collapsed header so a
 * lead with notes is still distinguishable from one without, and the user can
 * expand it per visit.
 */
export function LeadNotesSection({ leadId }: LeadNotesSectionProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { data: notes, isLoading, refetch } = useLeadNotes(leadId);
  const { mutate: addNote, isLoading: isAdding, error } = useAddLeadNote(leadId);

  const handleAddNote = async (noteText: string) => {
    await addNote(noteText);
    refetch(); // Refresh notes list
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="bg-card rounded-lg px-5 py-6">
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            className="w-full flex items-center justify-between p-0 hover:bg-transparent h-auto"
          >
            <div className="flex items-center gap-2">
              <h3 className="text-xl font-semibold text-secondary">Notes</h3>
              {!isLoading && (
                <Badge variant="secondary" data-testid="notes-count">
                  {notes.length}
                </Badge>
              )}
            </div>
            <ChevronDown
              className={`h-5 w-5 text-gray-500 transition-transform ${
                isOpen ? 'rotate-180' : ''
              }`}
            />
          </Button>
        </CollapsibleTrigger>

        <CollapsibleContent className="mt-4 space-y-4">
          <AddNoteForm
            onSubmit={handleAddNote}
            isSubmitting={isAdding}
            error={error}
          />

          {isLoading ? (
            <p className="text-sm text-gray-500">Loading notes...</p>
          ) : notes.length === 0 ? (
            <p className="text-sm text-gray-500">No notes yet.</p>
          ) : (
            <div className="space-y-3">
              {notes.map((note) => (
                <NoteCard key={note.id} note={note} />
              ))}
            </div>
          )}
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
